/**
 * D5: Learner Preferences (sound, reducedMotion, dailyGoal)
 * Source: src/components/settings/SettingsPage.tsx
 */

import { USE_BACKEND } from '../helpers'

const LEARNER_PREFS_KEY = 'meester-djay-learner-prefs'

export interface LearnerPreferences {
  sound: boolean
  haptic: boolean
  reducedMotion: boolean
  dailyGoal: 5 | 10 | 20
  weeklyGoal: 3 | 5 | 7
  avatarUrl?: string
  hideCreateAssessmentTip?: boolean
  hideCreatePracticeTip?: boolean
  /**
   * Subject (module id) the learner picked during onboarding as their
   * first focus area. Consumed once by the home page on the next visit
   * so FloatingDjay can point at the chosen subject; cleared after use.
   * Format: a ModuleId like 'REK' / 'GET' / 'MET' / 'SPR' / 'TAA' / 'TIJD'.
   */
  firstFocusSubject?: string
}

const DEFAULT_LEARNER_PREFS: LearnerPreferences = {
  sound: true,
  haptic: true,
  reducedMotion: false,
  dailyGoal: 10,
  weeklyGoal: 3,
  hideCreateAssessmentTip: false,
  hideCreatePracticeTip: false,
}

export async function getLearnerPreferences(): Promise<LearnerPreferences> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented')
  }
  try {
    const raw = localStorage.getItem(LEARNER_PREFS_KEY)
    if (!raw) return { ...DEFAULT_LEARNER_PREFS }
    const parsed = JSON.parse(raw)
    return {
      sound: parsed.sound ?? true,
      haptic: parsed.haptic ?? true,
      reducedMotion: parsed.reducedMotion ?? false,
      dailyGoal: [5, 10, 20].includes(parsed.dailyGoal) ? parsed.dailyGoal : 10,
      weeklyGoal: [3, 5, 7].includes(parsed.weeklyGoal) ? parsed.weeklyGoal : 3,
      avatarUrl: parsed.avatarUrl ?? undefined,
      hideCreateAssessmentTip: parsed.hideCreateAssessmentTip ?? false,
      hideCreatePracticeTip: parsed.hideCreatePracticeTip ?? false,
      firstFocusSubject:
        typeof parsed.firstFocusSubject === 'string'
          ? parsed.firstFocusSubject
          : undefined,
    }
  } catch {
    return { ...DEFAULT_LEARNER_PREFS }
  }
}

export async function saveLearnerPreferences(
  prefs: LearnerPreferences,
): Promise<void> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented')
  }
  try {
    localStorage.setItem(LEARNER_PREFS_KEY, JSON.stringify(prefs))
  } catch {
    // silent
  }
}
